<template>
  <v-container>
    <v-tabs v-model="tab" background-color="secondary" dark>
      <v-tab>Attendance Book</v-tab>
      <v-tab>Attendance Report</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- Tab 1: Attendance Booking -->
      <v-tab-item>
        <v-list subheader>
          <template v-for="(edge, index) in this.clubInfoNew">
            <v-subheader :key="index">
              <v-select
                v-model="groupname"
                :rules="groupRules"
                required
                :hint="`${groupname}`"
                label="Select Group Name"
                :items="edge.node.team.edges"
                item-value="node.teamId"
                item-text="node.teamName"
              >
                {{ groupname }}
              </v-select>
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="date"
                    label="Select Date"
                    :rules="groupDate"
                    prepend-icon="event"
                    v-on="on"
                    readonly
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="date"
                  @input="menu = false"
                ></v-date-picker>
              </v-menu>
            </v-subheader>

            <template v-for="teams in edge.node.team.edges">
              <v-list-item
                avatar
                lazy-validation
                v-for="(athlete, i) in teams.node.athlete.edges.filter(
                  athlete =>
                    athlete.node.teamId == groupname &&
                    athlete.node.status == 'active'
                )"
                v-bind:key="athlete.node.athleteId"
                @click="
                  handleAddAttendance(
                    athlete.node.athleteId,
                    !teams.node.athlete.edges[i].value, // Set missed based on switch
                    date,
                    comments
                  )
                "
              >
                <v-list-item-content>
                  <v-list-item-avatar>
                    <v-avatar size="40">
                      <img
                        src="https://randomuser.me/api/portraits/lego/6.jpg"
                        alt="Avatar"
                      />
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-title>
                    {{ athlete.node.firstName }} {{ athlete.node.lastName }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    color="secondary"
                    v-model="teams.node.athlete.edges[i].value"
                  ></v-switch>
                </v-list-item-action>
              </v-list-item>
            </template>
          </template>
        </v-list>
      </v-tab-item>

      <!-- Tab 2: Pivot Absence Report -->
      <v-tab-item>
        <PivotTable
          :data="pivotTableData"
          :pivot-props="pivotProps"
          :renderers="pivotRenderers"
          :inProfileName="profileName"
          :date-fields="['effectiveDate']"
          @profile-selected="handleProfileChange"
        />
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { GET_ATTENDANCE_LIST } from "@/components/constants/graphql";
import PivotTable from "../common/Report/PIvotTable.vue";
import ScrollRenderer from "@vue-pivottable/scroll-renderer";
import PlotlyRenderer from "@vue-pivottable/plotly-renderer";

export default {
  components: {
    PivotTable
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      dateFrom: `${currentYear}-01-01`,
      dateTo: `${currentYear}-12-31`,
      tab: 0,
      allAttendance: [],
      groupname: "",
      comments: "",
      date: new Date().toISOString().substr(0, 10),
      menu: false,
      groupRules: [groupname => !!groupname || "Group is Required"],
      groupDate: [date => !!date || "Date is Required"],
      props: {
        profileName: {
          type: String,
          default: function() {
            return "";
          }
        }
      },
      // Pivot table configuration
      pivotProps: {
        aggregatorName: "Count",
        rendererName: "Table",
        rows: ["athleteName", "teamName"],
        cols: ["effectiveDate"],
        vals: ["missed"],
        colOrder: "value_a_to_z",
        rowOrder: "value_a_to_z"
      }
    };
  },
  computed: {
    ...mapGetters(["loading", "clubInfo", "user", "pivotProfiles"]),
    clubInfoNew() {
      return this.clubInfo.edges.filter(
        item => item.node.clubId == this.$route.params.club
      );
    },
    pivotRenderers() {
      return {
        Table: ScrollRenderer.Table,
        "Table Heatmap": ScrollRenderer["Table Heatmap"],
        "Table Col Heatmap": ScrollRenderer["Table Col Heatmap"],
        "Table Row Heatmap": ScrollRenderer["Table Row Heatmap"],
        "Grouped Column Chart": PlotlyRenderer["Grouped Column Chart"],
        "Stacked Column Chart": PlotlyRenderer["Stacked Column Chart"],
        "Grouped Bar Chart": PlotlyRenderer["Grouped Bar Chart"],
        "Stacked Bar Chart": PlotlyRenderer["Stacked Bar Chart"],
        "Line Chart": PlotlyRenderer["Line Chart"],
        "Dot Chart": PlotlyRenderer["Dot Chart"],
        "Area Chart": PlotlyRenderer["Area Chart"],
        "Scatter Chart": PlotlyRenderer["Scatter Chart"],
        "Multiple Pie Chart": PlotlyRenderer["Multiple Pie Chart"]
      };
    },
    pivotTableData() {
      const absences = this.allAttendance.filter(entry => {
        if (!entry.missed) return false;

        const entryDate = new Date(entry.effectiveDate);
        const from = new Date(this.dateFrom);
        const to = new Date(this.dateTo);

        return entryDate >= from && entryDate <= to;
      });

      return absences.map(entry => {
        const athleteName = entry.athlete
          ? `${entry.athlete.firstName} ${entry.athlete.lastName}`
          : this.getAthleteNameById(entry.athleteId);

        const teamName =
          entry.athlete && entry.athlete.team && entry.athlete.team.teamName
            ? entry.athlete.team.teamName
            : "Unknown Team";

        const effectiveDate = entry.effectiveDate.split("T")[0];

        return {
          athleteName,
          teamName,
          effectiveDate
          // missed: 1
        };
      });
    },
    pivotProfilesList() {
      if (this.pivotProfiles && this.pivotProfiles.edges) {
        return this.pivotProfiles.edges
          .filter(profile => profile.node.profileCategory === "Athletes")
          .sort((a, b) => a.node.profileName.localeCompare(b.node.profileName));
      }
      return [];
    }
  },
  watch: {
    profileSeqno(newVal) {
      if (newVal) {
        const profile = this.pivotProfilesList.find(
          p => p.node.profileSeqno === newVal
        );
        if (profile) {
          this.profileName = profile.node.profileName;
          this.updateRouter();
        }
      } else {
        this.profileName = "";
        this.updateRouter();
      }
    },
    "$route.query.profileName"(newProfileName) {
      if (newProfileName) {
        this.applyProfileByName(newProfileName);
      }
    },
    "$route.query.tab"(newTab) {
      if (newTab === "athleteattendance") {
        // Ensure we're on the Absence Report tab when URL indicates it
        this.tab = 1; // 1 is the index for Absence Report tab
      }
    }
  },
  methods: {
    ...mapActions(["addAttendance", "allPivotProfiles", "pivotPofilesAdd"]),
    handleAddAttendance(athleteId, status, date) {
      // status is inverted because ON means attended (missed: false)
      this.$store.dispatch("addAttendance", {
        attendanceId: 0,
        athleteId: athleteId,
        workoutId: 1,
        effectiveDate: date,
        missed: status, // Status is passed as true when switch is OFF (missed)
        comments: date,
        updateSource: "VueJS tst"
      });
    },
    getAthleteNameById(id) {
      // First try to find in clubInfoNew
      for (const edge of this.clubInfoNew) {
        for (const team of edge.node.team.edges) {
          const athlete = team.node.athlete.edges.find(
            a => a.node.athleteId === id
          );
          if (athlete) {
            return `${athlete.node.firstName} ${athlete.node.lastName}`;
          }
        }
      }

      // If not found anywhere, return generic name
      return `Athlete ${id}`;
    },
    async fetchAllAttendance() {
      try {
        const result = await this.$apollo.query({
          query: GET_ATTENDANCE_LIST,
          fetchPolicy: "network-only" // always fresh
        });

        this.allAttendance = result.data.allAttendance.edges.map(e => e.node);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    },
    // Profile management methods
    saveProfileClick() {
      this.editedProfile = Object.assign({}, this.defaultProfile);
      this.newProfileDialog = true;
    },

    closeProfileDialog() {
      this.newProfileDialog = false;
      this.editedProfile = Object.assign({}, this.defaultProfile);
    },

    saveProfile() {
      const profileData = {
        profileName: this.editedProfile.profileName,
        profileCategory: this.editedProfile.profileCategory,
        repCols: this.pivotProps.cols.join(","),
        repRows: this.pivotProps.rows.join(","),
        repVals: this.pivotProps.vals.join(","),
        agregatorName: this.pivotProps.aggregatorName,
        rendererName: this.pivotProps.rendererName,
        colorder: this.pivotProps.colOrder,
        roworder: this.pivotProps.rowOrder,
        valueFilter: JSON.stringify({}).replaceAll('"', '\\"'),
        updateSource: this.user ? this.user.userName : "UnloggedUser",
        profileSeqno: 0
      };

      this.pivotPofilesAdd(profileData)
        .then(() => {
          this.allPivotProfiles(); // Refresh profiles list
          this.profileName = this.editedProfile.profileName;
          this.updateRouter();
          this.closeProfileDialog();
        })
        .catch(error => {
          console.error("Error saving profile:", error);
        });
    },

    handleProfileChange(profileName) {
      this.profileName = profileName;
      this.updateRouter();
    },

    applyProfileByName(profileName) {
      if (!profileName || !this.pivotProfilesList.length) return;

      const profile = this.pivotProfilesList.find(
        p => p.node.profileName === profileName
      );
      if (profile) {
        this.profileSeqno = profile.node.profileSeqno;
        this.profileName = profileName;
      }
    },

    updateRouter() {
      const query = { tab: "athleteattendance" };

      if (this.tab === 1 && this.profileName) {
        query.profileName = this.profileName;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    customFilter(item, queryText) {
      const searchText = queryText.toLowerCase();
      return (
        (item.node.profileName &&
          item.node.profileName.toLowerCase().includes(searchText)) ||
        (item.node.profileCategory &&
          item.node.profileCategory.toLowerCase().includes(searchText))
      );
    }
  },
  async mounted() {
    await this.fetchAllAttendance();
    await this.allPivotProfiles(); // 🔥 ensure profiles are available

    // ✅ Delay logic until data is fetched
    if (this.$route.query.profileName) {
      this.applyProfileByName(this.$route.query.profileName);
    }

    if (this.$route.query.tab === "athleteattendance") {
      this.tab = 1;
    }

    this.clubInfoNew.forEach(edge => {
      edge.node.team.edges.forEach(team => {
        team.node.athlete.edges.forEach(athlete => {
          if (athlete.value === undefined) {
            athlete.value = true;
          }
        });
      });
    });
  }
};
</script>
